import { yieldToMain } from 'assets/core/js/common'
import HashManager from 'assets/core/js/module/hash-manager'
import { PartialsLoadingEvents } from 'assets/core/js/module/partials'
import type { Parameter, Parameters } from 'assets/core/js/module/hash-manager'
import type { Facets } from 'assets/core/js/page/product/search/facets'

const formName = 'filter_offers'

const checkInputWithValue = (name: string, value: string): void => {
  let inputEl = document.querySelector<HTMLInputElement>(`[name="${name}[]"][value="${String(value)}"]`)
  if (name === 'filter_offers[budgets]') {
    inputEl = document.querySelector<HTMLInputElement>(`[name="${name}"]`)
  }

  if (!inputEl) {
    return
  }

  inputEl.checked = true

  if (name === 'filter_offers[budgets]') {
    inputEl.value = value
  }

  const filterBlockEl = inputEl.closest('.filter__block')

  if (filterBlockEl?.classList.contains('dca-collapsible-block--collapsed')) {
    filterBlockEl.classList.remove('dca-collapsible-block--collapsed')
  }
}

const applyFilter = (key: string, value: Parameter): void => {
  const name = `${formName}[${key}]`

  if (Array.isArray(value)) {
    for (const item of value) {
      checkInputWithValue(name, item)
    }
  } else if (typeof value === 'object' && value !== null) {
    for (const [nestedKey, nestedValue] of Object.entries(value)) {
      applyFilter(nestedKey, nestedValue)
    }
  } else {
    checkInputWithValue(name, value.toString())
  }
}

const resetFilters = (updateHash = true): void => {
  const filtersForm = document.getElementById('filters-sort')

  if (filtersForm === null) {
    return
  }

  filtersForm.querySelectorAll<HTMLInputElement>(`input[name^="${formName}"]:checked`).forEach(function (el) {
    el.checked = false
  })

  if (updateHash) {
    HashManager.setParameters({ ...HashManager.getParameters(), ...{ filters: {} } }, true)
  }
}

export const sortAccommodations = async (facets: Facets): Promise<void> => {
  const partialEl = document.querySelector('[data-partial-id="results"]')
  if (partialEl) {
    partialEl.dispatchEvent(new CustomEvent(PartialsLoadingEvents.SHOW))
  }

  await facets.sortAccommodations((totalAccommodations) => {
    if (partialEl) {
      partialEl.dispatchEvent(new CustomEvent(PartialsLoadingEvents.HIDE))
    }

    const groupedFilteredNoResultsEl = document.getElementById('grouped-filtered-no-results')

    if (!groupedFilteredNoResultsEl) {
      return
    }

    if (totalAccommodations === 0) {
      groupedFilteredNoResultsEl.removeAttribute('hidden')
    } else {
      groupedFilteredNoResultsEl.setAttribute('hidden', 'hidden')
    }
  })
}

export const setFiltersFromHash = async (facets: Facets, sortAccs = true): Promise<void> => {
  // make sure to reset the checked property of all filters so back/forward display the correct results
  resetFilters(false)

  const filters = HashManager.getParameter<Parameters>('filters')

  if (!filters) {
    await sortAccommodations(facets)
    return
  }

  for (const [key, value] of Object.entries(filters)) {
    if (value !== null && value !== undefined) {
      applyFilter(key, value)
    }
  }

  if (sortAccs) {
    await sortAccommodations(facets)
  }
}

export const updateHashWithFilter = async (el: HTMLInputElement): Promise<void> => {
  await yieldToMain()
  const name = el.getAttribute('name')?.replace(new RegExp(`${formName}\\[(.*?)\\](?:\\[\\])?$`), '$1')
  const isMultipleValues = el.getAttribute('name')?.endsWith('[]')

  if (!name) {
    return
  }

  const activeFilters = HashManager.getParameter<Parameter>('filters') ?? {}

  if (typeof activeFilters !== 'object' || Array.isArray(activeFilters) || activeFilters === null) {
    return
  }

  if (el.checked) {
    if (!isMultipleValues) {
      activeFilters[name] = el.value
    } else {
      const existingValues = activeFilters[name] || []
      activeFilters[name] = [...new Set([...existingValues, el.value])]
    }
  } else {
    if (!isMultipleValues) {
      delete activeFilters[name]
    } else if (Array.isArray(activeFilters[name])) {
      const updatedValues = activeFilters[name].filter((value) => value !== el.value)

      if (updatedValues.length > 0) {
        activeFilters[name] = updatedValues
      } else {
        delete activeFilters[name]
      }
    }
  }

  HashManager.setParameters({ ...HashManager.getParameters(), ...{ filters: activeFilters } }, true)
}

export default function (facets: Facets): void {
  const filtersForm = document.getElementById('filters-sort')

  if (filtersForm === null) {
    return
  }

  document.body.addEventListener('click', (e) => {
    const target = e.target as HTMLElement

    if (target.classList.contains('reset-filters')) {
      resetFilters(true)
      void (async () => {
        await sortAccommodations(facets)
      })()
    }
  })

  filtersForm.querySelectorAll<HTMLInputElement>(`input[name^="${formName}"]`).forEach(function (el) {
    el.addEventListener('change', function () {
      void (async () => {
        await sortAccommodations(facets)
        await updateHashWithFilter(el)

        const scrollTo = document.getElementById('booking')
        scrollTo?.scrollIntoView({
          block: 'start',
          inline: 'nearest',
          behavior: 'smooth',
        })
      })()
    })
  })
}
